import { useEffect, useState } from "react";
import { X, MapPin, Landmark, Brush, Home, AlertTriangle } from "lucide-react";
import { CLEANING_RING, SEVERITY } from "@/lib/constants";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProblemForm } from "@/pages/ApartmentDetail";

export default function MapSiteCard({ site, onClose }) {
  const { user } = useAuth();
  const [problems, setProblems] = useState([]);
  const isHq = site?.type === "hq";
  const companyHex = CLEANING_RING[site?.cleaning_company]?.hex || "#64748B";

  const loadProblems = () => {
    if (!site) return;
    api.get(`/problems?site_id=${site.id}&include_resolved=false`)
      .then((r) => setProblems(r.data))
      .catch(() => {});
  };
  useEffect(loadProblems, [site?.id]);

  if (!site) return null;

  return (
    <div data-testid="map-site-card"
         className="absolute z-[1001] top-[4.5rem] right-3 w-[340px] max-w-[calc(100vw-1.5rem)] max-md:top-auto max-md:bottom-[4.5rem] max-md:left-2 max-md:right-2 max-md:w-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[calc(100%-7rem)]">
      <div className="relative flex-shrink-0">
        {isHq && site.photo_url ? (
          <img src={site.photo_url} alt={site.name} className="w-full h-36 object-cover" />
        ) : (
          <div className="w-full h-24 flex items-center justify-center"
               style={{ background: isHq ? "#0F172A" : `${companyHex}14` }}>
            {isHq ? <Landmark className="w-8 h-8 text-amber-400" /> : <Brush className="w-8 h-8" style={{ color: companyHex }} />}
          </div>
        )}
        <button onClick={onClose} data-testid="map-site-card-close"
                className="absolute top-2.5 right-2.5 bg-white rounded-full p-1.5 shadow hover:bg-slate-50">
          <X className="w-4 h-4 text-slate-700" />
        </button>
      </div>
      <div className="p-4 space-y-3 overflow-y-auto thin-scroll">
        <div>
          <span data-testid="map-site-card-type"
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold text-white"
                style={{ background: isHq ? "#B8860B" : companyHex }}>
            {isHq ? <Landmark className="w-3 h-3" /> : <Brush className="w-3 h-3" />}
            {isHq ? "QG — Bureau général" : `Bureau — ${site.cleaning_company || "ménage"}`}
          </span>
          <h3 className="text-base font-bold font-display text-slate-900 leading-tight mt-2">{site.name}</h3>
          <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            {site.address}{site.city ? `, ${site.city}` : ""}{site.postal_code ? ` ${site.postal_code}` : ""}
          </div>
        </div>
        {isHq && site.access_notes && (
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Accès</div>
            <p className="text-sm text-slate-700 whitespace-pre-line" data-testid="map-site-card-access">{site.access_notes}</p>
          </div>
        )}
        {isHq && site.notes && (
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Notes</div>
            <p className="text-sm text-slate-700 whitespace-pre-line" data-testid="map-site-card-notes">{site.notes}</p>
          </div>
        )}
        {!isHq && (
          <p className="text-xs text-slate-400 flex items-center gap-1.5">
            <Home className="w-3.5 h-3.5" />Seuls le nom, la société et l'adresse sont renseignés pour un bureau de ménage.
          </p>
        )}
        <div className="border-t border-slate-100 pt-3" data-testid="map-site-card-problems">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Anomalies ({problems.length})
            </div>
          </div>
          {problems.length > 0 && (
            <div className="space-y-1.5 mb-2.5">
              {problems.slice(0, 3).map((p) => {
                const s = SEVERITY[p.severity] || SEVERITY.mineur;
                return (
                  <div key={p.id} data-testid={`site-problem-${p.id}`}
                       className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-2.5 py-1.5">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.hex }} />
                    <span className="text-slate-700 truncate">{p.title}</span>
                    <span className="text-slate-400 ml-auto flex-shrink-0">{p.status}</span>
                  </div>
                );
              })}
              {problems.length > 3 && (
                <p className="text-xs text-slate-400">+ {problems.length - 3} autre{problems.length - 3 > 1 ? "s" : ""}…</p>
              )}
            </div>
          )}
          {user?.role !== "read_only" && (
            <ProblemForm site={site} onCreated={loadProblems} testid="site-signal-problem-btn" />
          )}
        </div>
      </div>
    </div>
  );
}
